import {
  Alert,
  Checkbox,
  Divider,
  ListItem,
  Text,
  TextInput,
} from "@fluidattacks/design";
import type { TFormMethods } from "@fluidattacks/design";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IAddCreditCardFormValues } from "./types";
import { findBusinessInfo } from "./utils";

import { Dropdown } from "components/dropdown";

interface ICommonFieldsAdditionFormProps
  extends Pick<
    TFormMethods<IAddCreditCardFormValues>,
    "setValue" | "register" | "watch" | "formState"
  > {
  groupsInfo: Record<string, Record<string, string | null>>;
  selectedGroups: string[];
  requiredBusinessInfo: boolean;
  isOnTrial: boolean;
}

const CommonFieldsAdditionForm = ({
  groupsInfo,
  register,
  setValue,
  selectedGroups,
  requiredBusinessInfo,
  isOnTrial,
  watch,
  formState,
}: Readonly<ICommonFieldsAdditionFormProps>): JSX.Element => {
  const { errors } = formState;
  const { t } = useTranslation();
  const selectedBilledGroups = watch("billedGroups", []);
  const checkAll = watch("checkAll");
  const groupsNames = useMemo(
    (): string[] => Object.keys(groupsInfo),
    [groupsInfo],
  );
  const handleSelectAllGroups = useCallback((): void => {
    const selection = checkAll ? [] : [...groupsNames];
    setValue("billedGroups", selection);
    const businessInfo = findBusinessInfo(selection, groupsInfo);
    setValue("businessId", businessInfo.businessId ?? "");
    setValue("businessName", businessInfo.businessName ?? "");
    setValue("checkAll", !checkAll, { shouldDirty: true });
  }, [groupsNames, setValue, groupsInfo, checkAll]);

  const onCheck = useCallback(
    (billedGroup: string): (() => void) => {
      return (): void => {
        const newBilledGroups = selectedBilledGroups.includes(billedGroup)
          ? selectedBilledGroups.filter(
              (selectedGroup): boolean => selectedGroup !== billedGroup,
            )
          : [...selectedBilledGroups, billedGroup];

        setValue("billedGroups", newBilledGroups, { shouldDirty: true });
        if (newBilledGroups.length !== groupsNames.length) {
          setValue("checkAll", false, { shouldDirty: true });
        }
      };
    },
    [groupsNames.length, selectedBilledGroups, setValue],
  );

  useEffect((): void => {
    const businessInfo = findBusinessInfo(selectedGroups, groupsInfo);
    setValue("businessId", businessInfo.businessId ?? "");
    setValue("businessName", businessInfo.businessName ?? "");
  }, [groupsInfo, selectedGroups, setValue]);

  return (
    <Fragment>
      <TextInput
        error={errors.email?.message}
        label={t("organization.billing.provideBillingEmail")}
        name={"email"}
        register={register}
        required={true}
        type={"email"}
      />
      {!isOnTrial && (
        <Fragment>
          <Divider />
          <Alert variant={"info"}>
            {t("organization.billing.segmentBillingInformation")}
          </Alert>
          <Text size={"md"}>
            {t("organization.billing.groupsToBeBilledSelectorLabel")}
          </Text>
          <Dropdown
            id={"billedGroups-dropdown"}
            initialSelection={{ value: selectedGroups.join(", ") }}
            items={[]}
            width={"100%"}
          >
            <ListItem key={"selectAll"} value={""}>
              <Checkbox
                {...register("checkAll")}
                label={t("organization.billing.selectAll")}
                onChange={handleSelectAllGroups}
              />
            </ListItem>
            {groupsNames.map(
              (groupName): JSX.Element => (
                <ListItem key={groupName} value={""}>
                  <Checkbox
                    {...register("billedGroups")}
                    aria-label={groupName}
                    key={groupName}
                    label={groupName}
                    onChange={onCheck(groupName)}
                    value={groupName}
                  />
                </ListItem>
              ),
            )}
          </Dropdown>
          <Divider />
        </Fragment>
      )}

      {isOnTrial || requiredBusinessInfo || selectedGroups.length ? (
        <Fragment>
          <TextInput
            error={errors.businessId?.message}
            label={t("organization.billing.businessId")}
            name={"businessId"}
            register={register}
            required={requiredBusinessInfo}
          />
          <TextInput
            error={errors.businessName?.message}
            label={t("organization.billing.businessName")}
            name={"businessName"}
            register={register}
            required={requiredBusinessInfo}
          />
        </Fragment>
      ) : null}
      <Checkbox
        {...register("makeDefault")}
        label={t(
          "organization.tabs.billing.paymentMethods.add.creditCard.default",
        )}
      />
    </Fragment>
  );
};

export { CommonFieldsAdditionForm };
